import { useMemo } from "react";
import type { EncounterSpecialty } from "@/api/encounters";
import {
  isOpcs4SubsidiaryCode,
  isOutOfEndoscopyRangeSiteCode,
  outOfEndoscopyRangeSiteCodeMessage,
  parseCanonicalIcd10Code,
} from "@smartmed/codes";
import { findDuplicates } from "@smartmed/collections";
import {
  ageMismatch,
  ageMismatchText,
  diagnosisCodeWarnings,
  isKnownSex,
  isRecordedAge,
  sexMismatch,
  sexMismatchText,
} from "@smartmed/diagnosis-rules";
import type { DiffStatus } from "./admin-coding/codeComparison";
import { HistoricCodeBadge } from "./human-coding/HistoricCodeBadge";
import { pairedCodeWarningsFor } from "./pairedCodeWarnings";
import {
  DUPLICATE_ICD10_CODE_WARNING_TEXT,
  MISSING_ASTERISK_CODE_WARNING_TEXT,
  ORPHANED_ASTERISK_CODE_WARNING_TEXT,
  PRIMARY_DIAGNOSIS_WARNING_TEXT,
  PRIMARY_PROCEDURE_WARNING_TEXT,
  WarningFlag,
} from "./PrimaryDiagnosisWarning";
import { useCodeRestrictions } from "./useCodeRestrictions";
import { useDaggerAsteriskChecks } from "./useDaggerAsteriskChecks";

export type InlineFlag = { kind: "historic" } | { kind: "warning"; text: string };

export type FlaggableCode = { code: string; diffStatus?: DiffStatus };

const warning = (text: string): InlineFlag => ({ kind: "warning", text });

const isFlag = (value: InlineFlag | false | null | undefined | ""): value is InlineFlag => !!value;

export const historicCodeSet = (diagnoses: readonly { code: string; isHistoricCode?: boolean }[] | undefined) =>
  new Set((diagnoses ?? []).filter((diagnosis) => diagnosis.isHistoricCode).map((diagnosis) => diagnosis.code));

const isActive = (item: FlaggableCode) => item.diffStatus !== "removed";

export type FlaggableProcedure = FlaggableCode & { description?: string | null; aiFlagText?: string | null };

export const procedureInlineFlags = (
  procedure: FlaggableProcedure,
  index: number,
  specialty: EncounterSpecialty | null | undefined,
): InlineFlag[] =>
  isActive(procedure)
    ? [
        index === 0 && isOpcs4SubsidiaryCode(procedure.code) && warning(PRIMARY_PROCEDURE_WARNING_TEXT),
        specialty === "endoscopy" &&
          isOutOfEndoscopyRangeSiteCode(procedure.code) &&
          warning(outOfEndoscopyRangeSiteCodeMessage(procedure.code, procedure.description ?? "")),
        procedure.aiFlagText && warning(procedure.aiFlagText),
      ].filter(isFlag)
    : [];

type InlineDiagnosisFlagsInput = {
  diagnoses: readonly FlaggableCode[];
  specialty: EncounterSpecialty | null | undefined;
  patientAge: number | null | undefined;
  patientSex: string | null | undefined;
  historicCodes?: ReadonlySet<string>;
  incorrectPrimaryCode: string | null;
};

/** Every inline flag for a column of ICD-10 codes; rows marked removed are skipped by the rules and carry none. */
export const useInlineDiagnosisFlags = ({
  diagnoses,
  specialty,
  patientAge,
  patientSex,
  historicCodes,
  incorrectPrimaryCode,
}: InlineDiagnosisFlagsInput) => {
  const active = useMemo(
    () =>
      diagnoses.flatMap((diagnosis, displayIndex) =>
        isActive(diagnosis) ? [{ code: diagnosis.code, displayIndex }] : [],
      ),
    [diagnoses],
  );
  const activeCodes = useMemo(() => active.map(({ code }) => code), [active]);

  const { data: daggerAsteriskChecks, isError: daggerAsteriskCheckFailed } = useDaggerAsteriskChecks(activeCodes);
  const canCheckRestrictions = isKnownSex(patientSex) || isRecordedAge(patientAge);
  const { data: codeRestrictions, isError: restrictionCheckFailed } = useCodeRestrictions(
    canCheckRestrictions ? activeCodes : [],
  );

  const flagsByIndex = useMemo(() => {
    const duplicateCodes = new Set(findDuplicates(activeCodes));
    const byDisplayIndex = (warnings: readonly { index: number; message: string }[]) =>
      new Map(
        warnings.flatMap(({ index, message }) => {
          const activeDiagnosis = active[index];
          return activeDiagnosis ? [[activeDiagnosis.displayIndex, message] as const] : [];
        }),
      );
    const examinationWarnings = byDisplayIndex(
      diagnosisCodeWarnings(activeCodes.map((code) => parseCanonicalIcd10Code(code))),
    );
    const pairedCodeWarnings = byDisplayIndex(pairedCodeWarningsFor(specialty, activeCodes));

    return diagnoses.map((diagnosis, index): InlineFlag[] => {
      if (!isActive(diagnosis)) return [];

      const { code } = diagnosis;
      const restriction = codeRestrictions?.get(code);
      const sexMismatchForCode = sexMismatch(restriction?.genderRestriction, patientSex);
      const ageMismatchForCode = ageMismatch(restriction, patientAge);
      const examinationWarning = examinationWarnings.get(index);
      const pairedCodeWarning = pairedCodeWarnings.get(index);

      return [
        index === 0 && code === incorrectPrimaryCode && warning(PRIMARY_DIAGNOSIS_WARNING_TEXT),
        historicCodes?.has(code) && ({ kind: "historic" } as const),
        duplicateCodes.has(code) && warning(DUPLICATE_ICD10_CODE_WARNING_TEXT),
        daggerAsteriskChecks?.orphanedAsterisks.has(code) && warning(ORPHANED_ASTERISK_CODE_WARNING_TEXT),
        daggerAsteriskChecks?.daggersMissingAsterisk.has(code) && warning(MISSING_ASTERISK_CODE_WARNING_TEXT),
        examinationWarning && warning(examinationWarning),
        pairedCodeWarning && warning(pairedCodeWarning),
        sexMismatchForCode && warning(sexMismatchText(sexMismatchForCode)),
        ageMismatchForCode && warning(ageMismatchText(ageMismatchForCode)),
      ].filter(isFlag);
    });
  }, [
    diagnoses,
    active,
    activeCodes,
    specialty,
    patientAge,
    patientSex,
    historicCodes,
    incorrectPrimaryCode,
    daggerAsteriskChecks,
    codeRestrictions,
  ]);

  return { flagsByIndex, daggerAsteriskCheckFailed, restrictionCheckFailed };
};

export const InlineFlags = ({ flags }: { flags: readonly InlineFlag[] }) =>
  flags.map((flag) =>
    flag.kind === "historic" ? <HistoricCodeBadge key="historic" /> : <WarningFlag key={flag.text} text={flag.text} />,
  );
